/**
 * @fileoverview Resolves data-bound values for renderer validation surfaces.
 * @module inspection-renderer/validation
 *
 * Responsibilities:
 * - Apply the same metadata and source-resolution contract as DataBoundField.
 * - Share context-aware answered-value evaluation across validation surfaces.
 *
 * Dependencies:
 * - Core-domain field metadata and renderer ResolveContext.
 *
 * Architecture:
 * - Pure renderer adapter; source reads remain host-injected and synchronous.
 *
 * Notes:
 * - Only data-bound fields interpret `__DB__:` values as unresolved tokens;
 *   prefixed text answers remain ordinary saved values.
 */
import { DB_TOKEN_PREFIX, mergeDataBoundWidgetMeta, isFieldAnswered } from '@firesquirrel/core-domain';
import type { InspectionFormField } from '@firesquirrel/core-domain';
import type { ResolveContext } from '../contracts/resolveContext';

/**
 * @process isUnresolvedDbSentinel
 * Steps: Compare data-bound values with the shared unresolved token prefix.
 * Inputs: Field metadata and an unknown response or resolved source value.
 * Outputs: True when a data-bound value is a synthetic unresolved token.
 * Failure Handling: Non-data-bound fields and non-string inputs return false.
 * Idempotency: REQUIRED.
 */
export function isUnresolvedDbSentinel(field: InspectionFormField, value: unknown): boolean {
  return field.type === 'dataBound' && typeof value === 'string' && value.startsWith(DB_TOKEN_PREFIX);
}

/**
 * @process isUnansweredDataBoundValue
 * Steps: Check whether a saved bound response has an empty value.
 * Inputs: An unknown response value.
 * Outputs: True for undefined, null, or an empty string.
 * Failure Handling: Other values, including false and zero, remain answered.
 * Idempotency: REQUIRED.
 */
export function isUnansweredDataBoundValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * @process resolveFieldValueForValidation
 * Steps: Preserve ordinary answers; filter sentinel values only for data-bound fields, then resolve bound data.
 * Inputs: Field, raw value, N/A state, and optional host resolver context.
 * Outputs: The applicable response value or its non-empty context-resolved value.
 * Failure Handling: Missing metadata or resolver leaves the normalized response unchanged.
 * Idempotency: REQUIRED.
 */
export function resolveFieldValueForValidation(
  field: InspectionFormField,
  value: unknown,
  isNA: boolean | undefined,
  context: ResolveContext | undefined,
): unknown {
  const valueForValidation = isUnresolvedDbSentinel(field, value) ? undefined : value;
  if (field.type !== 'dataBound' || isFieldAnswered(field, valueForValidation, isNA) || !context) {
    return valueForValidation;
  }

  const extras = (field.extras ?? {}) as Record<string, unknown>;
  const widgetId = typeof extras.widgetId === 'string' ? extras.widgetId : undefined;
  const registered = widgetId ? context.resolveWidgetDef?.(widgetId) : undefined;
  const meta = mergeDataBoundWidgetMeta(registered, {
    widgetId,
    dataSource: typeof extras.dataSource === 'string' ? extras.dataSource : undefined,
    boundField: typeof extras.boundField === 'string' ? extras.boundField : undefined,
    compositeFields: Array.isArray(extras.compositeFields)
      ? extras.compositeFields.filter((part): part is string => typeof part === 'string')
      : undefined,
    displayFormat: typeof extras.displayFormat === 'string' ? extras.displayFormat : undefined,
    inputType: typeof extras.inputType === 'string' ? extras.inputType : undefined,
    defaultMode: typeof extras.defaultMode === 'string' ? extras.defaultMode : undefined,
    lookupType: typeof extras.lookupType === 'string' ? extras.lookupType : undefined,
  });
  if (!meta.dataSource || !context.resolveBoundField) return valueForValidation;

  const resolved = context.resolveBoundField({
    dataSource: meta.dataSource,
    boundField: meta.boundField ?? '',
    widgetId: meta.widgetId,
    displayFormat: meta.displayFormat,
    composite: meta.compositeFields,
    serviceAreaTypeCode: typeof extras.serviceAreaTypeCode === 'string'
      ? extras.serviceAreaTypeCode
      : undefined,
    entityCategoryFilter: typeof extras.entityCategoryFilter === 'string'
      ? extras.entityCategoryFilter
      : undefined,
  });
  const resolvedValue = isUnresolvedDbSentinel(field, resolved) ? undefined : resolved;
  return isFieldAnswered(field, resolvedValue, isNA) ? resolvedValue : valueForValidation;
}
